<template>
  <div :id="domId" class="editable" v-if="inline">{{ content }}</div>
  <textarea :id="domId" class="editable" v-else>{{ content }}</textarea>
</template>

<script>
// TinyMCE 5.10 self-hosted bundle.
// Vendored in-tree to replace the removed `vue-tinymce-editor` package.
// Keeps the same prop surface the rest of the app expects (id, value, inline,
// readonly, toolbar1/2, plugins, other_options), so call sites didn't change.
//
// Pinned to TinyMCE 5.10 (not 8.x) because vue-cli-service 3.6 ships an
// `@babel/preset-env` from early 2019 that can't parse the ES2022 syntax
// TinyMCE 6+ emits. TinyMCE 5 uses the same oxide skin / silver theme as v6+,
// so the wrapper is otherwise unchanged.

import tinymce from 'tinymce/tinymce';

// Silver theme and default icon pack (same names in v5 as v6+).
import 'tinymce/themes/silver';
import 'tinymce/icons/default';

// Skin + content CSS bundled statically so TinyMCE doesn't try to fetch them
// at runtime (would break under webpack without a CopyPlugin).
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/content/default/content.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';

// Minimal plugin set for a simple markup-enabled text input.
// Only what's needed for lists, inline formatting, headings and links.
// Notably excluded: table + quickbars (their quickinsert toolbar let users
// drop an HTML <table> into mj-text blocks, producing nested tables that
// broke the MJML-rendered email HTML); media/image/codesample/emoticons/etc
// (not relevant for plain text blocks in email templates).
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';

export default {
  name: 'tinymce',
  props: {
    htmlClass: { default: '', type: String },
    value: { default: '' },
    id: { default: '' },
    plugins: {
      default() {
        return ['advlist autolink lists link'];
      },
      type: Array,
    },
    toolbar1: {
      default:
        'formatselect | bold italic underline strikethrough | bullist numlist outdent indent | link | removeformat',
      type: String,
    },
    toolbar2: { default: '', type: String },
    other_options: {
      default() {
        return {};
      },
      type: Object,
    },
    readonly: { default: false, type: Boolean },
    inline: { default: false, type: Boolean },
  },
  data() {
    return {
      content: '',
      editor: null,
      checkerTimeout: null,
      isTyping: false,
    };
  },
  mounted() {
    this.content = this.value;
    this.init();
  },
  beforeDestroy() {
    if (this.editor !== null) {
      this.editor.destroy();
    }
  },
  computed: {
    domId() {
      return `mce_${this.id}_${this._uid}`;
    },
  },
  watch: {
    value(newValue) {
      if (!this.isTyping) {
        if (this.editor !== null) {
          this.editor.setContent(newValue || '');
        } else {
          this.content = newValue;
        }
      }
    },
    readonly(value) {
      if (this.editor) {
        this.editor.mode.set(value ? 'readonly' : 'design');
      }
    },
    id() {
      this.content = this.value;
      this.init();
    },
  },
  methods: {
    init() {
      const options = {
        selector: `#${this.domId}`,
        // We've imported the skin + content CSS statically above; tell TinyMCE
        // not to try to load them dynamically from baseURL.
        skin: false,
        content_css: false,
        branding: false,
        menubar: false,
        toolbar1: this.toolbar1,
        toolbar2: this.toolbar2,
        plugins: this.plugins,
        inline: this.inline,
        setup: this.setupEditor,
      };
      tinymce.init(Object.assign({}, options, this.other_options));
    },
    setupEditor(editor) {
      this.editor = editor;

      editor.on('KeyUp', () => {
        this.submitNewContent();
      });

      editor.on('Change', (e) => {
        if (editor.getContent() !== this.value) {
          this.submitNewContent();
        }
        this.$emit('editorChange', e);
      });

      editor.on('init', () => {
        editor.setContent(this.content || '');
        this.$emit('input', this.content);
        if (this.readonly) {
          editor.mode.set('readonly');
        }
      });

      this.$emit('editorInit', editor);
    },
    submitNewContent() {
      this.isTyping = true;
      if (this.checkerTimeout !== null) {
        clearTimeout(this.checkerTimeout);
      }
      this.checkerTimeout = setTimeout(() => {
        this.isTyping = false;
      }, 300);
      this.$emit('input', this.editor.getContent());
    },
  },
};
</script>
